import { toast } from 'sonner'
import i18next from 'i18next'

// Default configurations
const DEFAULT_DURATION = 3000
const DEFAULT_MESSAGES = {
  pending: 'Processing...',
  genericSuccess: 'Operation completed successfully',
  genericError: 'An error occurred',
}

/**
 * Get common toast options with dynamic position based on language.
 * @returns {Object} Common toast options.
 */
const getToastOptions = (customOptions = {}) => {
  const language = i18next.language || 'en'
  return {
    position: language === 'ar' ? 'bottom-left' : 'bottom-right',
    duration: DEFAULT_DURATION,
    ...customOptions,
  }
}

/**
 * Display a toast notification.
 * @param {Function} toastType - The toast method (e.g., toast.success, toast.error).
 * @param {string} message - The message to display.
 * @param {Object} options - Additional toast options.
 */
const notify = (toastType, message, options = {}) => {
  toastType(message, getToastOptions(options))
}

export const notifySuccess = (message, options = {}) =>
  notify(toast.success, message || DEFAULT_MESSAGES.genericSuccess, options)

export const notifyError = (message, options = {}) =>
  notify(toast.error, message || DEFAULT_MESSAGES.genericError, options)

export const notifyInfo = (message, options = {}) =>
  notify(toast.info, message, options)

export const notifyWarning = (message, options = {}) =>
  notify(toast.warning, message, options)

/**
 * Display a toast notification for promise resolutions.
 * @param {Promise} promise - The promise to monitor.
 * @param {Object} messages - An object containing pending, success, and error messages.
 * @param {Object} options - Additional toast options.
 */
export const notifyPromise = (promise, messages = {}, options = {}) => {
  return toast.promise(
    promise,
    {
      pending: messages.pending || DEFAULT_MESSAGES.pending,
      success: (data) =>
        typeof messages.success === 'function'
          ? messages.success(data)
          : messages.success || DEFAULT_MESSAGES.genericSuccess,
      error: (error) =>
        typeof messages.error === 'function'
          ? messages.error(error)
          : messages.error ||
            `${DEFAULT_MESSAGES.genericError}: ${error?.message || ''}`,
    },
    getToastOptions(options),
  )
}

/**
 * Display a toast notification for loading states.
 * @param {string} message - The loading message.
 * @param {Object} options - Additional toast options.
 * @returns {string} The toast ID.
 */
export const notifyLoading = (message, options = {}) => {
  return toast.loading(message || DEFAULT_MESSAGES.pending, {
    ...getToastOptions(options),
    duration: Infinity,
    dismissible: false,
    draggable: false,
  })
}

/**
 * Update a loading toast notification by dismissing it and showing a new one.
 * @param {string} id - The toast ID to dismiss.
 * @param {Object} options - The options for the new toast.
 */
export const updateToast = (id, options = {}) => {
  toast.dismiss(id)

  if (options.type === 'success') {
    notifySuccess(options.content)
  } else if (options.type === 'error') {
    notifyError(options.content)
  } else {
    notifyInfo(options.content)
  }
}
